import queryString from 'query-string';
import { generatePath } from 'react-router-dom';

import { ACH_BOARD_ACK_GROUP_URL, ACH_BOARD_ISSUE_ACK_URL, ACH_BOARD_URL } from '../../constants/ach/apiUrls';
import { achGetJSON, achSendJSON } from './client';

// Types are grounded to the reshaped GET /board dashboard read model (ACH-ak86,
// design Part 1). The response carries faceted counts + a contract -> line-item ->
// issue tree + per-contract pagination. Ack endpoints are unchanged: single-issue
// ack is PATCH /board/issues/{id}/acknowledge, bulk-by-rule is POST /board/acknowledge.

export type BoardSeverity = 'blocker' | 'critical' | 'warning' | 'info';
export type BoardState = 'open' | 'acknowledged' | 'resolved';
export type BoardResolutionReason = 'recovered' | 'no_longer_applicable';

// A person attached to an issue — the trafficker (ad-ops owner) or the account
// manager; null on the issue when nobody holds that role.
export interface BoardPerson {
  id: number;
  name: string;
  email?: string;
}

export interface BoardIssue {
  id: number;
  rule_id: string;
  title: string;
  severity: BoardSeverity;
  category: string;
  origin: string;
  state: BoardState;
  first_seen: string;
  last_seen: string;
  campaign_id: number;
  campaign_name: string;
  trafficker: BoardPerson | null;
  // Optional because the ACH server field ships separately (ACH-877): absent on
  // deployments predating it, null when the contract has no account manager.
  account_manager?: BoardPerson | null;
  acknowledged_by: string | null;
  // Display name of the acknowledging user; optional because the ACH server field
  // ships separately — the row falls back to acknowledged_by (the numeric id) when absent.
  acknowledged_by_name?: string | null;
  acknowledged_at: string | null;
  resolution_reason: BoardResolutionReason | null;
  resolved_at: string | null;
  evidence: string;
}

export interface BoardLineItem {
  insertion_order_id: number;
  // Joined at read time; null when the IO row is missing (FE falls back to `IO <id>`).
  io_name: string | null;
  issues: BoardIssue[];
}

export type SeverityCounts = Record<BoardSeverity, number>;

export interface BoardContract {
  contract_id: number;
  contract_name: string;
  severity_counts: SeverityCounts;
  line_items: BoardLineItem[];
}

// One person who holds at least one role on the filtered board, with the roles they
// hold and their issue count. Roles are an open list of wire names — a role added
// server-side needs no FE change.
export interface BoardPersonFacet {
  id: number;
  name: string;
  roles: string[];
  count: number;
}

// Faceted, scope-truthful counts (never page-scoped). Each facet applies all current
// filters EXCEPT its own dimension: by_severity ignores the severity filter (keeps
// state), by_state ignores the state filter (keeps severity). Never recompute these
// client-side from the returned rows.
export interface BoardCounts {
  by_severity: SeverityCounts;
  by_state: Record<BoardState, number>;
  // Faceted issue-type counts keyed by rule_id (ignores the rule_id filter, like the
  // other facets). Optional: absent on ACH deployments predating the by_rule facet —
  // the FE hides the issue-type control entirely when it's missing.
  by_rule?: Record<string, number>;
  // Faceted people roster (ignores the people filter, like the other facets), so
  // picking one person never removes the others from the control. Optional: absent on
  // ACH deployments predating it — the FE falls back to scraping the returned rows.
  by_person?: BoardPersonFacet[];
  unacked_high_sev_over_24h: number;
}

export interface BoardPagination {
  page: number;
  per_page: number;
  total_contracts: number;
}

export interface BoardResponse {
  counts: BoardCounts;
  contracts: BoardContract[];
  pagination: BoardPagination;
}

// BoardFilters mirrors the GET /board query params. People scope is expressed as
// people_id (repeatable), which the server ORs across every role; people_unassigned is
// the separate bucket for issues with nobody in ANY role and cannot be combined with it
// (the API 422s people_id alongside any unassigned people param).
export interface BoardFilters {
  severity?: BoardSeverity;
  state?: string;
  // Repeatable issue-type filter; each rule_id is serialized as its own query param.
  rule_id?: string[];
  category?: string;
  origin?: string;
  campaign_id?: number;
  insertion_order_id?: number;
  contract_id?: number;
  // Repeatable people filter; matches an issue whose trafficker OR account manager
  // is any of the ids.
  people_id?: number[];
  // Issues with nobody in any role. Replaces the older trafficker_unassigned param.
  people_unassigned?: boolean;
  page?: number;
  per_page?: number;
}

// buildBoardQuery serializes filters to a query string, dropping empty values.
// Guard: people_id and people_unassigned are mutually exclusive on the wire
// (the API returns 422 if both are set), so sending both is a caller bug — fail
// fast here rather than let the request 422.
export const buildBoardQuery = (filters: BoardFilters): string => {
  if (filters.people_id?.length && filters.people_unassigned) {
    throw new Error('buildBoardQuery: people_id and people_unassigned are mutually exclusive');
  }
  const params: Record<string, string | number | boolean | string[] | number[]> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return;
      }
      // query-string repeats the key for each element (arrayFormat 'none').
      params[key] = value;
      return;
    }
    params[key] = value as string | number | boolean;
  });
  return queryString.stringify(params);
};

export const getBoard = (achApiHost: string, filters: BoardFilters): Promise<BoardResponse> => {
  const query = buildBoardQuery(filters);
  const url = query ? `${ACH_BOARD_URL}?${query}` : ACH_BOARD_URL;
  return achGetJSON(achApiHost, url) as Promise<BoardResponse>;
};

// acknowledgeIssue acks one issue. PATCH per the machine contract (openapi.yaml
// operationId acknowledge-issue); the ack lifecycle was unchanged by the board reshape.
export const acknowledgeIssue = (achApiHost: string, id: number): Promise<BoardIssue> => {
  const path = generatePath(ACH_BOARD_ISSUE_ACK_URL, { id: String(id) });
  return achSendJSON(achApiHost, path, 'PATCH') as Promise<BoardIssue>;
};

export interface AckGroupBody {
  rule_id: string;
  contract_id?: number;
  insertion_order_id?: number;
}

// acknowledgeGroup bulk-acks every open issue sharing a rule within a scope
// (rule + contract for the "Ack all <rule> on this contract" action).
export const acknowledgeGroup = (achApiHost: string, body: AckGroupBody): Promise<unknown> =>
  achSendJSON(achApiHost, ACH_BOARD_ACK_GROUP_URL, 'POST', body);
